import { useMutation as useQueryMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { useEffect } from 'react'

function useMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(options: UseMutationOptions<TData, TError, TVariables, TContext>) {
  const mutation = useQueryMutation({ ...options, gcTime: 0 })
  const reset = mutation.reset
  useEffect(() => () => reset(), [reset])
  return mutation
}

import { apiRequest, downloadFile } from '../lib/apiClient'
import { useSession } from '../auth/useSession'
import type {
  AuditEntry,
  AuditQuery,
  ClientCreated,
  ClientUpdated,
  ClientView,
  CreateClientInput,
  CreateCredentialInput,
  CreateUserInput,
  CredentialSecretsInput,
  CredentialView,
  Dashboard,
  DashboardQuery,
  DataList,
  DeliveryQuery,
  DeliverySummary,
  DeliveryView,
  Exchange,
  KeyCreated,
  MerchantInput,
  MerchantPatch,
  MerchantView,
  Page,
  PaymentDetail,
  PaymentQuery,
  PaymentSummary,
  RejectionQuery,
  ResolveReviewInput,
  ServiceMeta,
  SessionResponse,
  TotpEnrollment,
  SetClientMerchantsInput,
  SimulateOutcome,
  UpdateClientInput,
  UpdateCredentialInput,
  UpdateUserInput,
  User,
  WebhookSecretRotated,
} from './apiTypes'

/**
 * Kunci cache TanStack Query. Setiap aksi yang mengubah data membatalkan kunci
 * yang terdampak supaya daftar dan detail langsung mencerminkan keadaan baru.
 */
export const queryKeys = {
  meta: ['meta'] as const,
  dashboard: (query: DashboardQuery) => ['dashboard', query] as const,
  payments: (query: PaymentQuery) => ['payments', query] as const,
  payment: (id: string) => ['payment', id] as const,
  deliveries: (query: DeliveryQuery) => ['webhook-deliveries', query] as const,
  rejections: (query: RejectionQuery) => ['inbound-rejections', query] as const,
  clients: ['clients'] as const,
  client: (id: string) => ['client', id] as const,
  merchants: ['merchants'] as const,
  merchant: (id: string) => ['merchant', id] as const,
  credentials: ['credentials'] as const,
  credential: (id: string) => ['credential', id] as const,
  users: ['users'] as const,
  auditLogs: (query: AuditQuery) => ['audit-logs', query] as const,
}

function useInvalidate(keys: readonly unknown[][]) {
  const queryClient = useQueryClient()
  return () => {
    for (const key of keys) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }
}

/* ---------- Meta ---------- */

export function useMeta() {
  return useQuery({
    queryKey: queryKeys.meta,
    queryFn: ({ signal }) => apiRequest<ServiceMeta>('/meta', { signal }),
    staleTime: 5 * 60 * 1000,
  })
}

/* ---------- Dashboard ---------- */

export function useDashboard(query: DashboardQuery) {
  return useQuery({
    queryKey: queryKeys.dashboard(query),
    queryFn: ({ signal }) =>
      apiRequest<Dashboard>('/dashboard', { query: { ...query }, signal }),
    placeholderData: (previous) => previous,
  })
}

/* ---------- Payment ---------- */

export function usePayments(query: PaymentQuery) {
  return useQuery({
    queryKey: queryKeys.payments(query),
    queryFn: ({ signal }) =>
      apiRequest<Page<PaymentSummary>>('/payments', {
        query: { ...query, status: query.status?.join(',') || undefined },
        signal,
      }),
  })
}

export function usePayment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.payment(id ?? ''),
    queryFn: ({ signal }) => apiRequest<PaymentDetail>(`/payments/${id}`, { signal }),
    enabled: Boolean(id),
  })
}

export function useRefreshPayment(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiRequest<PaymentDetail>(`/payments/${id}/refresh`, { method: 'POST' }),
    onSuccess: (detail) => {
      queryClient.setQueryData(queryKeys.payment(id), detail)
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useSimulatePayment(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (outcome: SimulateOutcome) =>
      apiRequest<PaymentDetail>(`/payments/${id}/simulate`, { method: 'POST', body: { outcome } }),
    onSuccess: (detail) => {
      queryClient.setQueryData(queryKeys.payment(id), detail)
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useResolveReview(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ResolveReviewInput) =>
      apiRequest<PaymentDetail>(`/payments/${id}/resolve-review`, { method: 'POST', body: input }),
    onSuccess: (detail) => {
      queryClient.setQueryData(queryKeys.payment(id), detail)
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

/** Ekspor CSV memakai rentang tanggal dan filter yang sedang berlaku. */
export function exportPayments(query: {
  from: string
  to: string
  merchant?: string
  status?: string
}) {
  return downloadFile('/payments/export.csv', query, 'tahupay-payments.csv')
}

/* ---------- Webhook dan inbound ---------- */

export function useDeliveries(query: DeliveryQuery) {
  return useQuery({
    queryKey: queryKeys.deliveries(query),
    queryFn: ({ signal }) =>
      apiRequest<Page<DeliverySummary>>('/webhook-deliveries', { query: { ...query }, signal }),
  })
}

export function useRedeliver(paymentId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<DeliveryView>(`/webhook-deliveries/${id}/redeliver`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] })
      if (paymentId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.payment(paymentId) })
      }
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useRejections(query: RejectionQuery) {
  return useQuery({
    queryKey: queryKeys.rejections(query),
    queryFn: ({ signal }) =>
      apiRequest<Page<Exchange>>('/inbound-rejections', { query: { ...query }, signal }),
  })
}

/* ---------- Client ---------- */

export function useClients() {
  const { user } = useSession()
  return useQuery({
    queryKey: queryKeys.clients,
    enabled: !user?.merchant_scope_restricted,
    queryFn: ({ signal }) => apiRequest<DataList<ClientView>>('/clients', { signal }),
  })
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.client(id ?? ''),
    queryFn: ({ signal }) => apiRequest<ClientView>(`/clients/${id}`, { signal }),
    enabled: Boolean(id),
  })
}

export function useCreateClient() {
  const invalidate = useInvalidate([['clients']])
  return useMutation({
    mutationFn: (input: CreateClientInput) =>
      apiRequest<ClientCreated>('/clients', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateClient(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: (input: UpdateClientInput) =>
      apiRequest<ClientUpdated>(`/clients/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  })
}

export function useSetClientMerchants(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: (input: SetClientMerchantsInput) =>
      apiRequest<ClientView>(`/clients/${id}/merchants`, { method: 'PUT', body: input }),
    onSuccess: invalidate,
  })
}

export function useCreateClientKey(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: () => apiRequest<KeyCreated>(`/clients/${id}/keys`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export function useRevokeClientKey(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: (keyId: string) =>
      apiRequest<void>(`/clients/${id}/keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}

export function useRotateWebhookSecret(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: () =>
      apiRequest<WebhookSecretRotated>(`/clients/${id}/webhook-secret/rotate`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export function useFinishWebhookSecret(id: string) {
  const invalidate = useInvalidate([['clients'], ['client', id]])
  return useMutation({
    mutationFn: () =>
      apiRequest<ClientView>(`/clients/${id}/webhook-secret/finish`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

/* ---------- Merchant ---------- */

export function useMerchants() {
  return useQuery({
    queryKey: queryKeys.merchants,
    queryFn: ({ signal }) => apiRequest<DataList<MerchantView>>('/merchants', { signal }),
  })
}

export function useMerchant(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.merchant(id ?? ''),
    queryFn: ({ signal }) => apiRequest<MerchantView>(`/merchants/${id}`, { signal }),
    enabled: Boolean(id),
  })
}

export function useCreateMerchant() {
  const invalidate = useInvalidate([['merchants']])
  return useMutation({
    mutationFn: (input: MerchantInput) =>
      apiRequest<MerchantView>('/merchants', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateMerchant(id: string) {
  const invalidate = useInvalidate([['merchants'], ['merchant', id]])
  return useMutation({
    mutationFn: (input: MerchantPatch) =>
      apiRequest<MerchantView>(`/merchants/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  })
}

/* ---------- Kredensial ---------- */

export function useCredentials() {
  return useQuery({
    queryKey: queryKeys.credentials,
    queryFn: ({ signal }) => apiRequest<DataList<CredentialView>>('/credentials', { signal }),
  })
}

export function useCredential(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.credential(id ?? ''),
    queryFn: ({ signal }) => apiRequest<CredentialView>(`/credentials/${id}`, { signal }),
    enabled: Boolean(id),
  })
}

export function useCreateCredential() {
  const invalidate = useInvalidate([['credentials']])
  return useMutation({
    mutationFn: (input: CreateCredentialInput) =>
      apiRequest<CredentialView>('/credentials', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateCredential(id: string) {
  const invalidate = useInvalidate([['credentials'], ['credential', id]])
  return useMutation({
    mutationFn: (input: UpdateCredentialInput) =>
      apiRequest<CredentialView>(`/credentials/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  })
}

export function useSetCredentialSecrets(id: string) {
  const invalidate = useInvalidate([['credentials'], ['credential', id]])
  return useMutation({
    mutationFn: (input: CredentialSecretsInput) =>
      apiRequest<CredentialView>(`/credentials/${id}/secrets`, { method: 'PUT', body: input }),
    onSuccess: invalidate,
  })
}

export function useTestCredential(id: string) {
  return useMutation({
    mutationFn: () => apiRequest<{ ok: boolean }>(`/credentials/${id}/test`, { method: 'POST' }),
  })
}

export function useEnrollTotp() {
  return useMutation({ mutationFn: (password: string) => apiRequest<TotpEnrollment>('/auth/totp/enroll', { method: 'POST', body: { password } }) })
}

export function useConfirmTotp() {
  return useMutation({ mutationFn: (code: string) => apiRequest<SessionResponse>('/auth/totp/confirm', { method: 'POST', body: { code } }) })
}

export function useDisableTotp() {
  return useMutation({ mutationFn: (input: { password: string; code: string }) => apiRequest<void>('/auth/totp/disable', { method: 'POST', body: input }) })
}

/* ---------- Pengguna ---------- */

export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: ({ signal }) => apiRequest<DataList<User>>('/users', { signal }),
  })
}

export function useCreateUser() {
  const invalidate = useInvalidate([['users']])
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      apiRequest<User>('/users', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateUser(id: string) {
  const invalidate = useInvalidate([['users']])
  return useMutation({
    mutationFn: (input: UpdateUserInput) =>
      apiRequest<User>(`/users/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  })
}

/* ---------- Audit ---------- */

export function useAuditLogs(query: AuditQuery) {
  return useQuery({
    queryKey: queryKeys.auditLogs(query),
    queryFn: ({ signal }) =>
      apiRequest<Page<AuditEntry>>('/audit-logs', { query: { ...query }, signal }),
  })
}
